import React, { Dispatch, SetStateAction, useCallback, useState } from 'react'
import debounce from 'lodash/debounce'
import { GET_EXTENDED_FAVORITES } from '@dustin-web/microsite-data/src/graphql/product/get-customer-product-lists.graphql'
import { useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks/use-graphql'
import { InputType, TextInput } from '@dustin-web/microsite-components/src/text-input'
import { useSWRConfig } from '@dustin-web/microsite-data/src/swr'
import { useI18n } from '@dustin-web/microsite-components/src/i18n'
import {
  UpdateCartItemCommentMutation,
  UpdateCartItemCommentMutationVariables,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { getCacheKeyFromDocument } from '@dustin-web/microsite-data/src/hooks/graphql-fetcher'
import { useUniqueId } from '@dustin-web/microsite-components/src/hooks'
import Button from '@dustin-web/microsite-components/src/button'
import { UPDATE_CART_ITEM_COMMENT } from '@dustin-web/microsite-data/src/graphql/product/update-cart-item-comment.graphql'
import { Comment } from './add-note'

type Props = {
  active: boolean
  comment: Comment
  cartItemId: number
  inFavorites?: boolean
  disableEditing?: boolean
  setActive: Dispatch<SetStateAction<boolean>>
}

export const AddNoteInput = ({
  active,
  comment,
  cartItemId,
  inFavorites = false,
  disableEditing = false,
  setActive,
}: Props) => {
  const [updateCartItemComment] = useGraphqlMutation<
    UpdateCartItemCommentMutation,
    UpdateCartItemCommentMutationVariables
  >(UPDATE_CART_ITEM_COMMENT)

  const { mutate } = useSWRConfig()
  const { translate } = useI18n()

  const [commentInputValue, setCommentInputValue] = useState(comment)

  const removeComment = () => {
    saveComment('')
    setCommentInputValue('')
  }

  const debounceSaveComment = useCallback(
    debounce((newValue: string) => {
      saveComment(newValue)
    }, 500),
    []
  )

  const saveComment = async (comment: Comment) => {
    await updateCartItemComment({
      variables: {
        cartItemId,
        value: comment,
      },
    })

    // Refetch base query
    if (inFavorites) {
      mutate(getCacheKeyFromDocument(GET_EXTENDED_FAVORITES))
    } else {
      mutate('GetCart')
    }
  }

  const onChangeComment = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCommentInputValue(e.target.value)
    debounceSaveComment(e.target.value ?? '')
  }

  const focusHandler = () => {
    if (disableEditing) {
      return
    }

    setActive(true)
  }

  const onBlurHandler = () => {
    setActive(false)
  }

  return (
    <div className="c-form c-form--dark u-fs-14 u-mb-8 d-print-none">
      <div className="u-relative">
        <TextInput
          id={useUniqueId('comment')}
          className="u-mb-0"
          inputClassName={active ? 'u-border-black' : ''}
          type={InputType.TEXT}
          label={translate('Notes')}
          hiddenLabel
          value={commentInputValue ?? ''}
          maxLength={255}
          onChange={onChangeComment}
          onFocus={focusHandler}
          onBlur={onBlurHandler}
          readOnly={disableEditing}
          autoFocus={active}
        />
        {!disableEditing && (
          <Button
            icon="close"
            className="c-btn--sm u-absolute u-border-none u-mb-0 i-16"
            onClick={removeComment}
            onFocus={focusHandler}
            onBlur={onBlurHandler}
            style={{ top: '5px', right: '0' }}
          />
        )}
      </div>
      <div className="u-pt-4 u-fs-12 u-color-gray-08">{translate('Checkout_OrderComment')}</div>
    </div>
  )
}
